<script>
export default {
    /**
     * Form validation
     *
     * Available attributs on input fields
     *
     * same: Boolean, add to input and the validator checks if the values are the same (max 2 on the same page)
     * live: Boolean, validation on blur
     * type: String, type validation (email, checkbox, phone, alpha [only alphabetical characters])
     * min: Number, length validation
     * max: Number, length validation
     * regex: Regular expression, if provided the validator will check the input according to the given expression
     * required: Boolean, form is not valid if one of the required input is empty
     * validate: String, search for errors in the api response (must be the corresponding key from the api respond)
     */

    name: 'FormValidation',

    props: {
        submitButton: null,
        instantSave: Boolean,
        onSubmit: Function,
        formErrors: {
            type: Array,
            default: undefined,
        },
    },
    data() {
        return {
            disabled: true,
            backups: [],
            touched: [],
            valid: null,
            dirty: null,
            sameValuesRequired: [],
            children: null,
            isValidNumber: null,
            submittedWithError: false,
        }
    },

    computed: {
        phoneFormat() {
            return this.$store.getters['site/phoneFormat']
        },
        violations() {
            return this.$store.getters['site/violations']
        },
        validForm() {
            return this.valid ? this.valid.every((el) => el === true) : false
        },
        dirtyForm() {
            return this.dirty ? this.dirty.includes(true) : false
        },
        submit() {
            return this.$slots.submit
                ? Array.from(this.$refs?.form?.children || []).find(
                      (child) => child.type === 'submit'
                  )
                : null
        },
    },

    watch: {
        violations: {
            deep: true,
            immediate: true,
            handler(violations) {
                if (violations) {
                    this.children?.forEach((child, index) => {
                        if ('validate' in child.$attrs) {
                            const violation = violations.find((violation) => {
                                return violation.propertyPath === child.$attrs.validate
                            })
                            if (violation) {
                                this.$set(
                                    this.valid,
                                    index,
                                    this.getValidity(child, violation.message)
                                )
                                this.$set(this.children[index], 'innerError', violation.message)
                            } else {
                                this.$set(this.valid, index, true)
                                this.$set(this.children[index], 'innerError', null)
                            }
                        }
                    })
                }
            },
        },
    },

    mounted() {
        this.setBackup()
    },
    beforeDestroy() {
        this.$store.dispatch('site/resetViolations')
    },
    methods: {
        setBackup() {
            this.children = this.$children.filter((child) => {
                return 'value' in child && 'innerError' in child
            })
            this.valid = Array(this.children.length).fill(true)
            this.dirty = Array(this.children.length).fill(false)
            this.touched = Array(this.children.length).fill(false)
            this.children.map((child, index) => {
                if (child.type === 'phone' && !this.isValidNumber) {
                    this.isValidNumber = require('libphonenumber-js').isValidNumber
                }
                if (!child.required) this.$set(this.dirty, index, true)
                if ('same' in child.$attrs) this.sameValuesRequired.push(child)
                this.$nextTick(() => {
                    this.setValidation(child, index)
                    this.checkState(child.value, child, index, true)
                    if (!!child.value && !!this.valid[index]) this.setError(index)
                })
            })
        },
        setValidation(child, index) {
            if (
                ('live' in child.$attrs || this.submittedWithError) &&
                child.type !== 'checkbox' &&
                !('immediate' in child.$attrs)
            ) {
                this.$nextTick(() => {
                    child.$on('blur', () => {
                        this.checkError(child, index)
                    })
                })
            }
            child.$on('input', ($event) => {
                if (
                    (('live' in child.$attrs || this.submittedWithError) &&
                        child.type === 'checkbox') ||
                    'immediate' in child.$attrs
                ) {
                    this.checkError(child, index)
                }
                if ('validate' in child.$attrs && this.violations) this.removeViolation(child)
                this.checkState($event, child, index)
            })
        },
        removeViolation(child) {
            let index = this.violations.findIndex(
                (violation) => violation.propertyPath === child.$attrs.validate
            )
            if (index > -1) this.$store.dispatch('site/removeViolation', index)
        },
        checkState(value, el, index, init) {
            if (!this.touched[index] && (!init || (!!value && !!this.valid[index])))
                this.$set(this.touched, index, true)
            const isValid = this.isValid(value, el)
            this.$set(this.valid, index, isValid === true || this.getValidity(el, isValid))
            if (!init) {
                this.$set(this.dirty, index, this.isDirty(value, this.backups[index]))
            }
            this.emitState()
        },
        isValid(value, { required, type, min, max, $attrs, placeholder }) {
            if (required && !this.validValue(value)) return this.$t('labels.error.field_required')
            if (type === 'email' && !this.validateEmail(value))
                return this.$t('labels.error.invalid_email')
            if (type === 'alpha' && !this.validateCharacters(value))
                return this.$t('labels.error.only_alpha')
            if (
                type === 'phone' &&
                !this.isValidNumber(value ? value : '', this.phoneFormat) &&
                (required || value)
            )
                return this.$t('labels.error.invalid_phone')
            if ($attrs.regex && !this.validateRegex(value, $attrs.regex))
                return this.$t('labels.error.invalid', { format: placeholder })
            if (this.validValue(value) && !this.validateMinLength(value, min))
                return this.$t('labels.error.invalid_length_short')
            if (this.validValue(value) && !this.validateMaxLength(value, max))
                return this.$t('labels.error.invalid_length_long', { max: max })
            if (type === 'checkbox') return (value && required) || !required
            return true
        },
        isDirty(value, backup) {
            return value === '' ? value === '' && this.validValue(backup) : value !== backup
        },
        setError(index) {
            if (this.children[index]) {
                this.$set(this.children[index], 'innerError', undefined)
                let isValid = this.valid[index]
                if (isValid !== true) {
                    this.$set(this.children[index], 'innerError', isValid.error)
                }
            }
        },
        checkError(el, index) {
            if (this.touched[index]) {
                this.$nextTick(() => {
                    this.setError(index)
                })
                if (
                    this.sameValuesRequired.find((sameEl) => sameEl._uid === el._uid) &&
                    (this.sameValuesRequired[0]._uid !== el._uid ||
                        (this.sameValuesRequired[0]._uid === el._uid &&
                            this.sameValuesRequired.find(
                                (sameEl) =>
                                    sameEl._uid !== this.sameValuesRequired[0]._uid &&
                                    this.validValue(sameEl.value)
                            )))
                ) {
                    let error = this.checkIfSame(false)
                    if (error && this.sameValuesRequired[0]._uid !== el._uid) {
                        this.valid[index] = this.getValidity(el, error)
                    }
                }
            }
        },
        checkIfSame(defaultError) {
            let error = defaultError
            if (this.sameValuesRequired.length) {
                let val
                this.sameValuesRequired.map((el, index) => {
                    if (index > 0) this.$set(el, 'innerError', undefined)
                    if (val === undefined) val = el.value
                    else if (val !== el.value) {
                        error = el.$attrs.same
                        this.$set(el, 'innerError', el.$attrs.same)
                    }
                })
            }
            return error
        },
        async formSubmit() {
            let error = this.disabled

            if (error) {
                this.submittedWithError = error
                this.children.map((child, index) => {
                    this.checkState(child.value, child, index)
                })
                this.valid.forEach((_, index) => {
                    this.setError(index)
                })
                error = this.disabled
                if (error) {
                    this.$emit('error')
                    return
                }
            }
            error = this.checkIfSame(error)
            if (!error) {
                try {
                    if (this.onSubmit) await this.onSubmit()
                    else this.$emit('submit')
                    this.submittedWithError = error
                    this.setBackup()
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(
                        `${e?.title || 'Error'}: ${
                            e?.detail || 'Submit funciont returned with error!'
                        }`
                    )
                }
            } else if (error && !this.submittedWithError) {
                this.submittedWithError = error
                this.children.map((child, index) => {
                    this.setValidation(child, index)
                })
                this.$emit('error')
            }
        },
        validValue(value) {
            return value !== null && value !== undefined && value !== ''
        },
        emitState() {
            this.disabled = !(this.validForm && (this.dirtyForm || this.instantSave))
            if (this.disabled) this.submit?.classList.add('disabled')
            else this.submit?.classList.remove('disabled')
            this.$emit('disable', this.disabled)
            this.$emit('valid', this.validForm)

            if (this.formErrors)
                this.$emit(
                    'update:formErrors',
                    this.valid.reduce((p, c, index) => {
                        if (this.touched[index] && c !== true) p.push(c)
                        return p
                    }, [])
                )
        },
        validateMinLength(value, min) {
            return this.validValue(value) ? (min ? value.length >= min : true) : false
        },
        validateMaxLength(value, max) {
            return this.validValue(value) ? (max ? value.length <= max : true) : false
        },
        validateEmail(email) {
            return this.$emailREGEX.test(email)
        },
        validateCharacters(value) {
            return this.$alphaREGEX.test(value)
        },
        validateRegex(value, regex) {
            regex = new RegExp(regex)
            return regex.test(value)
        },
        getValidity(el, isValid) {
            return {
                id: el.uid,
                label: el.$slots.default?.find((slot) => slot.text)?.text,
                error: typeof isValid === 'string' ? isValid : !isValid,
            }
        },
    },
}
</script>

<template lang="pug">
form(@submit.prevent="formSubmit" :id="`formValidation-${_uid}`" autocomplete="off" ref="form" novalidate)
    slot
    slot(name="submit")
</template>
